import { SortDescriptor } from "@zapier/design-system";
import {
  PublicAppWithImplementationsMetaAndStats,
  PrivateAppWithImplementationsMetaAndStats,
} from "./useHydrateAppsWithStats";

export type AppSortField =
  | "title"
  | "tags"
  | "has_stale_auth"
  | "auth_count"
  | "zap_count"
  | "member_count"
  | undefined;
export type AppSortDirection = "asc" | "desc";

export interface AppSortState {
  field: AppSortField;
  direction: AppSortDirection;
}

export type HydratedApp =
  | PublicAppWithImplementationsMetaAndStats
  | PrivateAppWithImplementationsMetaAndStats;

const SORTABLE_COLUMNS = new Set([
  "title",
  "tags",
  "has_stale_auth",
  "auth_count",
  "zap_count",
  "member_count",
]);

/**
 * Calculates the next sort state based on the current state and sort descriptor.
 */
export function getNextAppSortState(
  currentState: AppSortState,
  sortDescriptor: SortDescriptor,
): AppSortState {
  const column = String(sortDescriptor.column);

  if (!SORTABLE_COLUMNS.has(column)) {
    return currentState;
  }

  const tableDirection: AppSortDirection =
    sortDescriptor.direction === "ascending" ? "asc" : "desc";

  return { field: column as AppSortField, direction: tableDirection };
}

/**
 * Gets the display title for an app (lowercased for sorting).
 */
function getAppTitle(app: HydratedApp): string {
  return (app.title as string)?.toLowerCase() || "";
}

/**
 * Sorts apps by title.
 */
export function sortAppsByTitle(
  apps: HydratedApp[],
  direction: AppSortDirection = "asc",
): HydratedApp[] {
  const multiplier = direction === "desc" ? -1 : 1;

  return [...apps].sort((a, b) => {
    const titleA = getAppTitle(a);
    const titleB = getAppTitle(b);
    return titleA.localeCompare(titleB) * multiplier;
  });
}

/**
 * Gets the first badge label for an app (lowercased for sorting).
 * Returns empty string if no badges, so apps without badges sort first in ascending order.
 */
function getAppFirstBadgeLabel(app: HydratedApp): string {
  // Note: The type says `implementationsMeta` but the hydration code in
  // useHydrateAppsWithImplementationsMeta.ts sets `implementationMeta` (singular)
  const badges = (
    app as unknown as { implementationMeta?: { badges?: { label?: string }[] } }
  ).implementationMeta?.badges;
  if (!badges || badges.length === 0) {
    return "";
  }
  return (badges[0]?.label || "").toLowerCase();
}

/**
 * Sorts apps by tags (first badge label alphabetically).
 * Apps with no badges are sorted to the end in ascending order.
 */
export function sortAppsByTags(
  apps: HydratedApp[],
  direction: AppSortDirection = "asc",
): HydratedApp[] {
  const multiplier = direction === "desc" ? -1 : 1;

  return [...apps].sort((a, b) => {
    const tagA = getAppFirstBadgeLabel(a);
    const tagB = getAppFirstBadgeLabel(b);
    // Sort empty tags to the end when ascending, beginning when descending
    if (tagA === "" && tagB !== "") return multiplier;
    if (tagA !== "" && tagB === "") return -multiplier;
    return tagA.localeCompare(tagB) * multiplier;
  });
}

/**
 * Sorts apps by stale auth status (expired connections).
 * In descending order, apps with expired connections come first.
 */
export function sortAppsByStaleAuth(
  apps: HydratedApp[],
  direction: AppSortDirection = "desc",
): HydratedApp[] {
  const multiplier = direction === "desc" ? -1 : 1;

  return [...apps].sort((a, b) => {
    const hasStaleA = a.hasStaleAuth ? 1 : 0;
    const hasStaleB = b.hasStaleAuth ? 1 : 0;
    return (hasStaleA - hasStaleB) * multiplier;
  });
}

/**
 * Sorts apps by auth count (number of connections).
 */
export function sortAppsByAuthCount(
  apps: HydratedApp[],
  direction: AppSortDirection = "desc",
): HydratedApp[] {
  const multiplier = direction === "desc" ? -1 : 1;

  return [...apps].sort((a, b) => {
    const countA = a.stats?.authCount || 0;
    const countB = b.stats?.authCount || 0;
    return (countA - countB) * multiplier;
  });
}

/**
 * Sorts apps by zap count.
 */
export function sortAppsByZapCount(
  apps: HydratedApp[],
  direction: AppSortDirection = "desc",
): HydratedApp[] {
  const multiplier = direction === "desc" ? -1 : 1;

  return [...apps].sort((a, b) => {
    const countA = a.stats?.zapCount || 0;
    const countB = b.stats?.zapCount || 0;
    return (countA - countB) * multiplier;
  });
}

/**
 * Sorts apps by member count (connection users).
 */
export function sortAppsByMemberCount(
  apps: HydratedApp[],
  direction: AppSortDirection = "desc",
): HydratedApp[] {
  const multiplier = direction === "desc" ? -1 : 1;

  return [...apps].sort((a, b) => {
    const countA = a.stats?.memberCount || 0;
    const countB = b.stats?.memberCount || 0;
    return (countA - countB) * multiplier;
  });
}

/**
 * Sorts apps by the specified field.
 */
export function sortApps(
  apps: HydratedApp[],
  field: AppSortField,
  direction: AppSortDirection,
): HydratedApp[] {
  if (field === "title") {
    return sortAppsByTitle(apps, direction);
  }
  if (field === "tags") {
    return sortAppsByTags(apps, direction);
  }
  if (field === "has_stale_auth") {
    return sortAppsByStaleAuth(apps, direction);
  }
  if (field === "auth_count") {
    return sortAppsByAuthCount(apps, direction);
  }
  if (field === "zap_count") {
    return sortAppsByZapCount(apps, direction);
  }
  if (field === "member_count") {
    return sortAppsByMemberCount(apps, direction);
  }
  return apps;
}
